import requestUrlInfo from 'js/lib/requestUrlInfo';
import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useUserExperienceContext } from 'bundles/unified-common/contexts/UserExperienceContext';
import type { MembershipsForRedirect } from 'bundles/unified-common/utils/userExperienceRedirectUtils';
import {
  SelectionAction,
  getUserExperienceRedirectOrSetSelection,
} from 'bundles/unified-common/utils/userExperienceRedirectUtils';

const normalizePathname = (p: string) => (p === '' ? '/' : p);

type Props = {
  memberships: MembershipsForRedirect | null;
  isMembershipsLoading: boolean;
};

/**
 * Server-only branch for HTTP redirects (same pattern as `AdminRootRedirect` / `EnterprisePageGuard`:
 * after data is ready, call `redirectClientOrServer` during render — on SSR it throws `RedirectError`).
 *
 * Runs during the markup pass after `loadDataFromTree` when Apollo has cached UX + membership queries.
 * `SelectionAction.SET_SELECTION` is not handled here (mutations run on the client in `useUserExperienceRedirect`).
 */
const UserExperienceSsrRedirect = ({ memberships, isMembershipsLoading }: Props) => {
  const { pathname: routerPathname, search: routerSearch } = useLocation();
  const {
    userExperience,
    isLoading: isUserExperienceLoading,
    isUpdating: isUserExperienceUpdating,
  } = useUserExperienceContext();

  if (!inServerContext) {
    return null;
  }

  const shouldRunRedirectLogic = GrowthDiscoveryEpicClient.preview('orgScopedHome');
  if (
    !shouldRunRedirectLogic ||
    !isAuthenticatedUser() ||
    isUserExperienceLoading ||
    isUserExperienceUpdating ||
    isMembershipsLoading
  ) {
    return null;
  }

  const pathname = normalizePathname(routerPathname);
  const pathnameForRedirect = pathname;
  const search = routerSearch || '';
  const host = requestUrlInfo().hostname;

  const result = getUserExperienceRedirectOrSetSelection(
    pathname,
    pathnameForRedirect,
    search,
    host,
    userExperience,
    memberships
  );

  if (result?.action === SelectionAction.REDIRECT) {
    redirectClientOrServer(result.url);
  }

  return null;
};

export default UserExperienceSsrRedirect;
